import {
  formatMarketTime,
  getSingleDayHourlyTickDates,
  isSameCalendarDay,
} from '@helpers';
import * as d3 from 'd3';
import { useEffect, useRef } from 'react';
import styles from './MultiLineChartAxes.module.scss';

export type MultiLineChartAxesProps = {
  xScale: d3.ScaleTime<number, number>;
  yScale: d3.ScaleLinear<number, number>;
  yAxisFormatter: (n: number) => string;
};

const createDateAxis = (
  xScale: d3.ScaleTime<number, number>,
  interval: d3.TimeInterval | null
) =>
  d3
    .axisBottom<Date>(xScale)
    .ticks(interval)
    .tickSize(0)
    .tickPadding(16)
    .tickFormat(d3.timeFormat('%b %-d'));

const createIntradayAxis = (
  xScale: d3.ScaleTime<number, number>,
  startDate: Date,
  endDate: Date
) => {
  const tickValues = getSingleDayHourlyTickDates(startDate, endDate);
  if (tickValues.length === 0) {
    return d3
      .axisBottom<Date>(xScale)
      .tickSize(0)
      .tickPadding(16)
      .tickValues([]);
  }

  const lastTickTimestamp = tickValues[tickValues.length - 1].getTime();

  return d3
    .axisBottom<Date>(xScale)
    .tickSize(0)
    .tickPadding(16)
    .tickValues(tickValues)
    .tickFormat((value: Date) => {
      const suffix = value.getTime() === lastTickTimestamp ? ' ET' : '';
      return `${formatMarketTime(value)}${suffix}`;
    });
};

const DAY_MS = 24 * 60 * 60 * 1000;

const createXAxis = (xScale: d3.ScaleTime<number, number>) => {
  const [domainStart, domainEnd] = xScale.domain();

  if (isSameCalendarDay(domainStart, domainEnd)) {
    return createIntradayAxis(xScale, domainStart, domainEnd);
  }

  const spanDays = (domainEnd.getTime() - domainStart.getTime()) / DAY_MS;

  if (spanDays <= 21) {
    return createDateAxis(xScale, d3.timeDay.every(1));
  }

  if (spanDays < 42) {
    return createDateAxis(xScale, d3.timeMonday.every(1));
  }

  return d3
    .axisBottom<Date>(xScale)
    .ticks(d3.timeMonth.every(1))
    .tickSize(0)
    .tickPadding(16)
    .tickFormat(d3.timeFormat('%b'));
};

export const MultiLineChartAxes = ({
  xScale,
  yScale,
  yAxisFormatter,
}: MultiLineChartAxesProps) => {
  const xAxisRef = useRef<SVGGElement>(null);
  const yAxisRef = useRef<SVGGElement>(null);

  useEffect(() => {
    if (xAxisRef.current && xScale.range()[1] > 0) {
      d3.select(xAxisRef.current).call(createXAxis(xScale));
    }

    if (yAxisRef.current && yScale.range()[0] > 0) {
      const axisSelection = d3.select(yAxisRef.current);
      const axis = d3
        .axisRight<number>(yScale)
        .ticks(6)
        .tickSize(xScale.range()[1])
        .tickFormat(yAxisFormatter)
        .tickPadding(10);

      axisSelection.call(axis).call(g => g.select('.domain').remove());
    }
  }, [xScale, yScale, yAxisFormatter]);

  return (
    <g>
      <g
        className={styles.chartAxisG}
        ref={xAxisRef}
        transform={`translate(0, ${yScale.range()[0]})`}
        data-testid="chart-x-axis-group"
      />
      <g className={styles.chartAxisG} ref={yAxisRef} />
    </g>
  );
};
